import { useEffect, useMemo, useRef, useState } from "react";
import { geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { NAME_TO_CODE, PARTY, STATUS_LABEL, stateFill } from "./shared";

export function UsMap({ layer = {}, layerLabel = "", selected, onSelect }) {
  const [features, setFeatures] = useState(null);
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    import("us-atlas/states-albers-10m.json").then((m) => {
      const topo = m.default || m;
      if (alive) setFeatures(feature(topo, topo.objects.states).features);
    });
    return () => { alive = false; };
  }, []);

  const shapes = useMemo(() => {
    if (!features) return [];
    const path = geoPath();
    return features
      .map((f) => ({ code: NAME_TO_CODE[f.properties.name], name: f.properties.name, d: path(f) }))
      .filter((s) => s.code);
  }, [features]);

  const move = (e, s) => {
    const r = wrapRef.current.getBoundingClientRect();
    setHover({ ...s, x: e.clientX - r.left, y: e.clientY - r.top, w: r.width });
  };

  if (!features) {
    return <div className="aspect-[975/610] w-full animate-pulse bg-white/5 rounded" data-testid="midterms-map-loading" />;
  }

  return (
    <div ref={wrapRef} className="relative" data-testid="midterms-map">
      <svg viewBox="0 0 975 610" className="w-full h-auto" role="img" aria-label="Interactive map of US states">
        {shapes.map((s) => {
          const entry = layer[s.code];
          const isSel = selected === s.code;
          return (
            <path
              key={s.code}
              d={s.d}
              fill={stateFill(entry)}
              stroke={isSel ? "#FFD84D" : "#07112e"}
              strokeWidth={isSel ? 2.5 : 0.8}
              className="cursor-pointer outline-none transition-[opacity,filter] duration-150 hover:brightness-125 focus-visible:brightness-125"
              style={{ opacity: selected && !isSel ? 0.72 : 1 }}
              tabIndex={0}
              role="button"
              aria-label={s.name}
              data-testid={`midterms-map-state-${s.code}`}
              onPointerMove={(e) => e.pointerType === "mouse" && move(e, s)}
              onPointerLeave={() => setHover(null)}
              onClick={() => { setHover(null); onSelect?.(s.code); }}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect?.(s.code))}
            />
          );
        })}
      </svg>
      {hover && <MapTooltip hover={hover} entry={layer[hover.code]} layerLabel={layerLabel} />}
    </div>
  );
}

function MapTooltip({ hover, entry, layerLabel }) {
  const left = Math.min(hover.x + 14, hover.w - 190);
  const p = entry && PARTY[entry.party];
  const label = !entry ? `No ${layerLabel} race this year` : p ? `${p.name} · ${STATUS_LABEL[entry.status]}` : STATUS_LABEL.none;
  return (
    <div
      className="pointer-events-none absolute z-20 w-[180px] bg-white text-black px-3 py-2 shadow-xl text-xs"
      style={{ left, top: hover.y + 14 }}
      data-testid="midterms-map-tooltip"
    >
      <div className="font-serif-ed font-semibold text-sm leading-tight">{hover.name}</div>
      <div className="mt-1 flex items-center gap-1.5 uppercase tracking-[0.14em] text-[10px] text-neutral-600">
        {p && <span className="w-2 h-2 inline-block" style={{ background: p.solid }} />}
        {label}
      </div>
      <div className="mt-1 text-[10px] text-neutral-400">Click for full results</div>
    </div>
  );
}
